import {
  boolean,
  date,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Review Sources — connected Google/Facebook accounts for monitoring
// ---------------------------------------------------------------------------

export const REVIEW_SOURCE_TYPES = ["google_business_profile", "facebook_pages"] as const;
export type ReviewSourceType = (typeof REVIEW_SOURCE_TYPES)[number];

export const POLLING_STATES = ["active", "dormant", "reconnecting", "paused", "error"] as const;
export type PollingState = (typeof POLLING_STATES)[number];

export const reviewSources = pgTable("review_sources", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  sourceType: text("source_type").notNull(),
  sourceAccountId: text("source_account_id").notNull(),
  oauthIntegrationId: uuid("oauth_integration_id"),
  accountName: text("account_name"),
  config: jsonb("config").default({}).notNull(),
  storeReviewerNames: boolean("store_reviewer_names").notNull().default(true),
  status: text("status").notNull().default("active"),
  pollingState: text("polling_state").notNull().default("active"),
  lastPolledAt: timestamp("last_polled_at", { withTimezone: true }),
  lastNewReviewAt: timestamp("last_new_review_at", { withTimezone: true }),
  lastReconnectedAt: timestamp("last_reconnected_at", { withTimezone: true }),
  consecutiveFailures: integer("consecutive_failures").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_review_sources_tenant_source").on(table.tenantId, table.sourceType, table.sourceAccountId),
  index("idx_review_sources_tenant").on(table.tenantId),
  index("idx_review_sources_polling").on(table.pollingState, table.lastPolledAt),
]);

// ---------------------------------------------------------------------------
// Review Request Campaigns — configurable triggers for soliciting reviews
// ---------------------------------------------------------------------------

export const ALTERNATE_STRATEGIES = ["round_robin", "weight_by_count", "google_only", "facebook_only"] as const;
export type AlternateStrategy = (typeof ALTERNATE_STRATEGIES)[number];

export const REVIEW_WIDGET_STYLES = ["carousel", "wall", "badge", "ticker", "featured"] as const;
export type ReviewWidgetStyle = (typeof REVIEW_WIDGET_STYLES)[number];
export const REVIEW_WIDGET_DECISION_STATUSES = ["approved", "hidden"] as const;
export type ReviewWidgetDecisionStatus = (typeof REVIEW_WIDGET_DECISION_STATUSES)[number];

export const reviewRequestCampaigns = pgTable("review_request_campaigns", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  triggerEvent: text("trigger_event").notNull(),
  delayMinutes: integer("delay_minutes").notNull().default(120),
  channelPriority: text("channel_priority"),
  googleReviewUrl: text("google_review_url"),
  facebookReviewUrl: text("facebook_review_url"),
  alternateStrategy: text("alternate_strategy").notNull().default("round_robin"),
  templateId: text("template_id"),
  followUpEnabled: boolean("follow_up_enabled").notNull().default(true),
  followUpDelayDays: integer("follow_up_delay_days").notNull().default(3),
  maxFollowUps: integer("max_follow_ups").notNull().default(1),
  expiresAfterDays: integer("expires_after_days").notNull().default(30),
  dailySendLimit: integer("daily_send_limit").notNull().default(100),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  index("idx_rrc_tenant").on(table.tenantId),
  index("idx_rrc_trigger").on(table.tenantId, table.triggerEvent),
]);

// ---------------------------------------------------------------------------
// Review Requests — individual review solicitations sent to contacts
// ---------------------------------------------------------------------------

export const REVIEW_REQUEST_STATUSES = [
  "pending", "sent", "opened", "clicked", "reviewed", "expired",
] as const;
export type ReviewRequestStatus = (typeof REVIEW_REQUEST_STATUSES)[number];

export const reviewRequests = pgTable("review_requests", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  campaignId: uuid("campaign_id").notNull(),
  contactId: uuid("contact_id").notNull(),
  trackingToken: text("tracking_token").notNull(),
  channel: text("channel").notNull(),
  templateId: text("template_id"),
  reviewUrl: text("review_url").notNull(),
  reviewPlatform: text("review_platform").notNull(),
  status: text("status").notNull().default("pending"),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  sentAt: timestamp("sent_at", { withTimezone: true }),
  openedAt: timestamp("opened_at", { withTimezone: true }),
  clickedAt: timestamp("clicked_at", { withTimezone: true }),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  followUpCount: integer("follow_up_count").notNull().default(0),
  followUpSentAt: timestamp("follow_up_sent_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_review_requests_token").on(table.trackingToken),
  index("idx_rr_tenant_campaign").on(table.tenantId, table.campaignId),
  index("idx_rr_tenant_contact").on(table.tenantId, table.contactId),
  index("idx_rr_status_expires").on(table.tenantId, table.status, table.expiresAt),
  index("idx_rr_followup").on(table.tenantId, table.status, table.sentAt),
]);

// ---------------------------------------------------------------------------
// Review Intercepts — private landing-page ratings before public redirect
// ---------------------------------------------------------------------------

export const reviewIntercepts = pgTable("review_intercepts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  bookingId: uuid("booking_id").notNull(),
  personId: uuid("person_id").notNull(),
  serviceId: uuid("service_id").notNull(),
  rating: integer("rating").notNull(),
  feedback: text("feedback"),
  selectedPlatform: text("selected_platform"),
  redirectUrl: text("redirect_url"),
  submittedAt: timestamp("submitted_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_review_intercepts_booking").on(table.tenantId, table.bookingId),
  index("idx_review_intercepts_person").on(table.tenantId, table.personId, table.submittedAt),
  index("idx_review_intercepts_service").on(table.tenantId, table.serviceId, table.submittedAt),
]);

// ---------------------------------------------------------------------------
// Review Widget Configs — tenant-scoped embed widget settings + approvals
// ---------------------------------------------------------------------------

export const reviewWidgetConfigs = pgTable("review_widget_configs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  enabled: boolean("enabled").notNull().default(true),
  widgetStyle: text("widget_style").notNull().default("card"),
  allowedDomains: jsonb("allowed_domains").$type<string[]>().notNull().default([]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_review_widget_configs_tenant").on(table.tenantId),
  index("idx_review_widget_configs_enabled").on(table.tenantId, table.enabled),
]);

export const reviewWidgetReviewDecisions = pgTable("review_widget_review_decisions", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  reviewId: uuid("review_id").notNull(),
  status: text("status").notNull().default("approved"),
  decidedBy: uuid("decided_by").notNull(),
  decidedAt: timestamp("decided_at", { withTimezone: true }).defaultNow().notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_review_widget_review_decisions_review").on(table.tenantId, table.reviewId),
  index("idx_review_widget_review_decisions_status").on(table.tenantId, table.status, table.decidedAt),
]);

// ---------------------------------------------------------------------------
// Reviews — aggregated reviews from all monitoring sources
// ---------------------------------------------------------------------------

export const reviews = pgTable("reviews", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  sourceType: text("source_type").notNull(),
  sourceId: uuid("source_id").notNull(),
  externalReviewId: text("external_review_id").notNull(),
  rating: integer("rating").notNull(),
  text: text("text"),
  authorName: text("author_name"),
  dataSource: text("data_source").notNull(),
  isPublicData: boolean("is_public_data").notNull().default(true),
  publishedAt: timestamp("published_at", { withTimezone: true }).notNull(),
  respondedAt: timestamp("responded_at", { withTimezone: true }),
  responseText: text("response_text"),
  ratingBasedSentiment: text("rating_based_sentiment").notNull(),
  aiSentiment: text("ai_sentiment"),
  aiAspects: jsonb("ai_aspects"),
  // Moderation claim/release (RV-D): a single moderator owns a review while
  // triaging. Both columns are cleared on release. assignedTo is a free-form
  // text actor id (auth users live outside the tenant DB) — keep nullable.
  assignedTo: text("assigned_to"),
  claimedAt: timestamp("claimed_at", { withTimezone: true }),
  // Escalation queue (RV-F): when frontline moderators need a manager/specialist
  // to step in, the review is "escalated" with a specific assignee. Both
  // columns are cleared when the escalation is resolved (closed) via a normal
  // release/respond flow. Nullable so the default state is "not escalated".
  escalatedAt: timestamp("escalated_at", { withTimezone: true }),
  escalatedTo: text("escalated_to"),
  deletedAt: timestamp("deleted_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_reviews_external").on(table.tenantId, table.sourceType, table.externalReviewId),
  index("idx_reviews_tenant").on(table.tenantId),
  index("idx_reviews_source").on(table.tenantId, table.sourceId),
  index("idx_reviews_rating").on(table.tenantId, table.rating, table.publishedAt),
  index("idx_reviews_published").on(table.tenantId, table.publishedAt),
  index("idx_reviews_assigned").on(table.tenantId, table.assignedTo),
  // Escalation queue lookup — operators filter by "show me everything
  // escalated to me, newest first". Partial-style index keyed on the
  // assignee + timestamp keeps the queue scan tight.
  index("idx_reviews_escalated").on(table.tenantId, table.escalatedTo, table.escalatedAt),
]);

// ---------------------------------------------------------------------------
// Reviewer Bans (RV-F) — moderators block abusive / spam / harassing reviewer
// identities from being routed into operator queues. We never delete public
// review data (Google / Facebook own the source of truth there) — instead we
// keep an immutable ban list and filter at query time. `reviewerIdentifier`
// is a stable, opaque value (hashed email OR external reviewer id) provided
// by the moderator; storing raw author names would invite false positives.
// ---------------------------------------------------------------------------

export const reviewerBans = pgTable("reviewer_bans", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  // Stable identifier — typically a SHA-256 of a normalized email OR an
  // external reviewer id from Google/Facebook. Free-form text keeps the
  // table provider-agnostic; application-level validation enforces shape.
  reviewerIdentifier: text("reviewer_identifier").notNull(),
  reason: text("reason").notNull(),
  // Auth users live outside the tenant DB so we keep this as a uuid actor id
  // captured at ban time; no FK across the boundary.
  bannedBy: uuid("banned_by").notNull(),
  bannedAt: timestamp("banned_at", { withTimezone: true }).defaultNow().notNull(),
  // Nullable = permanent ban. Setting an expiry lets us reinstate a
  // reviewer automatically once a cooling-off window passes.
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  // One active ban per (tenant, reviewer). Re-banning the same identifier
  // should refresh the row (DELETE + re-INSERT) rather than collect dupes.
  uniqueIndex("uq_reviewer_bans_tenant_identifier")
    .on(table.tenantId, table.reviewerIdentifier),
  index("idx_reviewer_bans_tenant").on(table.tenantId),
  // Cleanup job filter: "find bans that have expired". `expiresAt` is
  // nullable so the index is composite with tenantId for partial pruning.
  index("idx_reviewer_bans_expires").on(table.tenantId, table.expiresAt),
]);

// ---------------------------------------------------------------------------
// Review Content Exports — review-to-content lineage + audit evidence
// ---------------------------------------------------------------------------

export const REVIEW_CONTENT_EXPORT_CHANNELS = ["social_draft"] as const;
export type ReviewContentExportChannel = (typeof REVIEW_CONTENT_EXPORT_CHANNELS)[number];

export const reviewContentExports = pgTable("review_content_exports", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  reviewId: uuid("review_id").notNull(),
  personId: uuid("person_id").notNull(),
  consentRecordId: uuid("consent_record_id").notNull(),
  channel: text("channel").notNull().default("social_draft"),
  socialPostId: uuid("social_post_id"),
  mediaUploadId: uuid("media_upload_id"),
  reviewerDisplayName: text("reviewer_display_name").notNull(),
  quoteText: text("quote_text").notNull(),
  starRating: integer("star_rating").notNull(),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().notNull().default({}),
  createdBy: uuid("created_by").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_review_content_exports_review").on(table.tenantId, table.reviewId),
  index("idx_review_content_exports_person").on(table.tenantId, table.personId, table.createdAt),
  index("idx_review_content_exports_consent").on(table.tenantId, table.consentRecordId),
]);

// ---------------------------------------------------------------------------
// Competitor Reviews — tracked Google Places competitors + recent snapshots
// ---------------------------------------------------------------------------

export const competitorProfiles = pgTable("competitor_profiles", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  googlePlaceId: text("google_place_id").notNull(),
  avgRatingX100: integer("avg_rating_x100").notNull().default(0),
  totalReviews: integer("total_reviews").notNull().default(0),
  lastFetchedAt: timestamp("last_fetched_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_competitor_profiles_place").on(table.tenantId, table.googlePlaceId),
  index("idx_competitor_profiles_fetch").on(table.tenantId, table.lastFetchedAt),
]);

export const competitorReviewSnapshots = pgTable("competitor_review_snapshots", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  competitorId: uuid("competitor_id").notNull(),
  externalReviewId: text("external_review_id").notNull(),
  rating: integer("rating").notNull(),
  text: text("text"),
  authorName: text("author_name"),
  publishedAt: timestamp("published_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_competitor_review_snapshots_external").on(table.competitorId, table.externalReviewId),
  index("idx_competitor_review_snapshots_competitor").on(table.tenantId, table.competitorId, table.publishedAt),
]);

// ---------------------------------------------------------------------------
// Reputation Daily Scores — pre-computed daily aggregates
// ---------------------------------------------------------------------------

export interface RatingDistribution {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}

export interface SentimentBreakdown {
  positive: number;
  neutral: number;
  negative: number;
}

export const reputationDailyScores = pgTable("reputation_daily_scores", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  date: date("date", { mode: "date" }).notNull(),
  sourceType: text("source_type").notNull(),
  avgRatingX100: integer("avg_rating_x100").notNull().default(0),
  totalReviews: integer("total_reviews").notNull().default(0),
  newReviews: integer("new_reviews").notNull().default(0),
  responseRateX100: integer("response_rate_x100").notNull().default(0),
  avgResponseTimeMinutes: integer("avg_response_time_minutes"),
  ratingDistribution: jsonb("rating_distribution").$type<RatingDistribution>().default({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }).notNull(),
  sentimentBreakdown: jsonb("sentiment_breakdown").$type<SentimentBreakdown>().default({ positive: 0, neutral: 0, negative: 0 }).notNull(),
  reviewVelocityX100: integer("review_velocity_x100").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  uniqueIndex("uq_rep_scores_date").on(table.tenantId, table.date, table.sourceType),
  index("idx_rep_scores_tenant_date").on(table.tenantId, table.date),
]);

// ---------------------------------------------------------------------------
// Review Response Templates (RV-E) — reusable reply boilerplates so moderators
// can pick a tone-matched starter instead of hand-typing every response.
// `ratingScope` lets templates target a rating bucket (e.g. negative-only
// apology language) while a default value of "any" means "applies to any
// rating". RV-C (#832) flagged the previous inline template path as a stub;
// this is the real persistence + lookup table.
// ---------------------------------------------------------------------------

export const REVIEW_RESPONSE_TEMPLATE_RATING_SCOPES = [
  "any",
  "positive",
  "neutral",
  "negative",
] as const;
export type ReviewResponseTemplateRatingScope =
  (typeof REVIEW_RESPONSE_TEMPLATE_RATING_SCOPES)[number];

export const reviewResponseTemplates = pgTable("review_response_templates", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  body: text("body").notNull(),
  // Bucket the template applies to. Stored as text rather than a PG enum so
  // adding new scopes later does not require a migration. Application-level
  // validation enforces the allowed set.
  ratingScope: text("rating_scope").notNull().default("any"),
  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  // Name unique within a tenant + scope so the same name can exist for
  // different scopes (e.g. "Thanks" for positive vs neutral).
  uniqueIndex("uq_review_response_templates_tenant_name_scope")
    .on(table.tenantId, table.name, table.ratingScope),
  index("idx_review_response_templates_tenant").on(table.tenantId),
  index("idx_review_response_templates_scope").on(table.tenantId, table.ratingScope),
]);

// ---------------------------------------------------------------------------
// Review Response Drafts (RV-G) — AI-drafted replies awaiting operator
// approval. The AI pipeline picks an RV-E template by rating scope,
// personalizes via the AI gateway, and persists the result with status
// "pending_approval". 1-3 star reviews are NEVER auto-sent (AI-D #781);
// the existing /reviews/:id/respond route is the only path that actually
// posts to the source platform. `template_id` is nullable so the
// pure-AI fallback path (no matching template) still produces a draft
// instead of dropping the review on the floor.
// ---------------------------------------------------------------------------

export const REVIEW_RESPONSE_DRAFT_STATUSES = [
  "pending_approval",
  "approved",
  "rejected",
] as const;
export type ReviewResponseDraftStatus =
  (typeof REVIEW_RESPONSE_DRAFT_STATUSES)[number];

export const reviewResponseDrafts = pgTable("review_response_drafts", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  reviewId: uuid("review_id").notNull(),
  templateId: uuid("template_id"),
  body: text("body").notNull(),
  status: text("status").notNull().default("pending_approval"),
  aiGenerated: boolean("ai_generated").notNull().default(false),
  // RV-022 — EU AI Act Article 50(2) transparency-label fields.
  //
  // Per `docs/vision_execution/openclaw/RV-022-DECISION-PACKET.md` (2026-05-14):
  //
  //   - `original_ai_body`: verbatim AI output before any operator edit (the
  //     wire shape needed to compute editorial control vs verbatim approval).
  //   - `final_body`: posted-to-provider body at approve time. May equal
  //     `original_ai_body` (verbatim approval, disclosure required) or differ
  //     (substantial edit ≥ 10 % Levenshtein, authorship transfers to operator).
  //   - `edit_distance_percent`: Levenshtein ratio between `original_ai_body`
  //     and `final_body`, on [0, 100]. The disclosure decision is computed
  //     deterministically from this value via the 10 %-threshold rule.
  //   - `disclosure_required`: locked at approve time; the provider plugin
  //     posts an AI disclosure label suffix iff true. Default NULL so legacy
  //     rows are not retroactively classified — the disclosure-formatter
  //     service writes this column on approval.
  //
  // All four columns are nullable so existing pending-approval rows from RV-G
  // (PR #881) load without a forced rationale; the disclosure-formatter
  // service backfills `original_ai_body` for AI-drafted rows and writes
  // `final_body`, `edit_distance_percent`, `disclosure_required` at approve
  // time. Audit retention is 6 years per AI Act Article 50.
  originalAiBody: text("original_ai_body"),
  finalBody: text("final_body"),
  editDistancePercent: integer("edit_distance_percent"),
  disclosureRequired: boolean("disclosure_required"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  // Enforced in code as a partial unique (pending only) via the SQL
  // migration; Drizzle's table builder doesn't model partial uniques,
  // so we keep the regular composite index for query planner symmetry.
  index("idx_review_response_drafts_review").on(table.tenantId, table.reviewId),
  index("idx_review_response_drafts_tenant_status").on(table.tenantId, table.status, table.createdAt),
]);

// ---------------------------------------------------------------------------
// Review Themes (RV-019 Phase 1) — per-review topic labels emitted by the
// AI ingestion pipeline. Stored row-per-(review, theme) so the `/intelligence`
// aggregation can compute "top themes by frequency + average rating" without
// re-running the extractor at query time. Tenant-scoped + indexed on the
// canonical theme label so dashboards can read top-N theme frequency in O(log n).
// Confidence is `numeric_x100` stored as integer to keep cross-driver math
// integer-only (Postgres returns numerics as strings via postgres.js).
// ---------------------------------------------------------------------------

export const reviewThemes = pgTable("review_themes", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  reviewId: uuid("review_id").notNull(),
  theme: text("theme").notNull(),
  // Confidence score scaled to 0-100 (integer). 0 = AI refused; 100 = certain.
  confidenceX100: integer("confidence_x100").notNull().default(0),
  // Identifies the extractor + model + prompt version that produced the row.
  // Required so we can re-run extraction on a model upgrade without dupes
  // (delete WHERE source = "ai-pipeline-v1" then re-insert with v2).
  source: text("source").notNull().default("ai-pipeline-v1"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [
  // One row per (tenant, review, theme, source) — re-running the extractor
  // for the same source is idempotent via UPSERT.
  uniqueIndex("uq_review_themes_review_theme")
    .on(table.tenantId, table.reviewId, table.theme, table.source),
  index("idx_review_themes_tenant_theme").on(table.tenantId, table.theme),
  index("idx_review_themes_review").on(table.tenantId, table.reviewId),
]);

export type ReviewThemeRecord = typeof reviewThemes.$inferSelect;
export type ReviewThemeInsert = typeof reviewThemes.$inferInsert;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function computeRatingBasedSentiment(rating: number): "positive" | "neutral" | "negative" {
  if (rating >= 4) return "positive";
  if (rating === 3) return "neutral";
  return "negative";
}
